import { Capacitor } from "@capacitor/core";
import type React from "react";
import { useEffect } from "react";
import {
	identifyPostHog,
	initPostHog,
	isPostHogEnabled,
	registerPostHog,
	resetPostHog,
} from "../../lib/posthog";

interface EraPostHogProviderProps {
	children: React.ReactNode;
	userId: string;
	userEmail?: string;
}

export function EraPostHogProvider({
	children,
	userId,
	userEmail,
}: EraPostHogProviderProps) {
	useEffect(() => {
		// No-op when disabled (no token) — SDK never loads
		if (!isPostHogEnabled()) {
			return;
		}

		let cancelled = false;

		// initPostHog lazy-loads the SDK, so identify must wait for it to resolve
		initPostHog().then(() => {
			if (cancelled) {
				return;
			}
			// Compute device context once so identify and register use identical
			// values (and we don't call into Capacitor twice).
			const platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'
			const isNative = Capacitor.isNativePlatform();
			// Identify with Auth0 `sub`. Email is included as a person property
			// (BAA-covered) so PostHog person records are recognizable in the
			// dashboard. platform/isNative are non-PHI device metadata.
			identifyPostHog(userId, {
				...(userEmail ? { email: userEmail } : {}),
				platform,
				isNative,
			});
			// Stamp every event with the runtime platform (super properties) so
			// app traffic is distinguishable from web — autocaptured events too.
			// isNative=true → native app (iOS/Android); false → web browser.
			registerPostHog({ platform, isNative });
		});

		// Clear identity on logout / user switch so sessions never bleed together
		return () => {
			cancelled = true;
			resetPostHog();
		};
	}, [userId, userEmail]);

	return <>{children}</>;
}
